import * as React from "react";
import * as React44ms from "../../Util/form-utils/react-44ms";

import {
  CheckboxInput2023,
  CheckboxInput2023Expects,
  CheckboxInput2023Props,
} from "../../Common";
import { AuthorizeDeliveryToBeLeftWithoutSignatureFormLogic } from "../forms";
import { getFreeDeliveryThreshold } from "../model";

import {
  useDataMutableSourceShippingClientStorageData,
  useDeliveryDetailsUserExperience_asController,
  useMyBagPresentationModel,
} from "../providers";
import { useMyPreviouslyFilledInInfo } from "./use-my-previously-filled-in-info";

interface ShippingMethodProps {
  formRef_authorizeDelivery: React44ms.Api.TypedRef<CheckboxInput2023Expects>;
}
export const ShippingMethod: React.FunctionComponent<ShippingMethodProps> = (
  props,
) => {
  const { presentationMyBagOrder } = useMyBagPresentationModel();
  const {
    handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange:
      deliveryDetailsUserExperience_asController_handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
  } = useDeliveryDetailsUserExperience_asController();

  useRememberMyDetails(props.formRef_authorizeDelivery);

  const handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange: CheckboxInput2023Props["onChange"] =
    React.useCallback(
      (formRef, value) => {
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          formRef,
          value,
        );
        deliveryDetailsUserExperience_asController_handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange(
          value,
        );
      },
      [
        deliveryDetailsUserExperience_asController_handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange,
      ],
    );

  const shippingValueBreakdown =
    presentationMyBagOrder?.shippingValueBreakdown;
  const freeDeliveryThreshold = getFreeDeliveryThreshold();

  if (presentationMyBagOrder?.isThisAVirtualOrder) {
    return null;
  }

  return (
    <section className="shipping-method">
      <h2>{`Shipping Method`}</h2>

      {/* No misleading "$0.00 Standard Shipping" flash while totals load. */}
      {shippingValueBreakdown && (
        <div className="shipping-method-input-container">
          <input
            type="radio"
            id="shipping_method"
            name="shipping_method"
            defaultChecked
            checked={true}
          />
          <label htmlFor="shipping_method">
            {shippingValueBreakdown.isFreeShipping ? (
              <React.Fragment>
                <span>{`\$`}</span>
                <span>{`0.00`}</span>
                <span>{` Free Shipping (FREE for orders over \$${freeDeliveryThreshold})`}</span>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span>{`\$`}</span>
                <span>
                  {`${shippingValueBreakdown.shippingTotal.toFixed(2)}`}
                </span>
                <span>{` Standard Shipping (FREE for orders over \$${freeDeliveryThreshold})`}</span>
              </React.Fragment>
            )}
          </label>
        </div>
      )}

      <AuthorizeDeliveryToBeLeftWithoutSignatureFormLogic
        ref={props.formRef_authorizeDelivery}
      />
      <CheckboxInput2023
        formRef={props.formRef_authorizeDelivery}
        onChange={
          handleLastSelected_authoriseDeliveryToBeLeftWithoutSignatureChange
        }
        id="authoriseDeliveryToBeLeftWithoutSignature"
      >{`Untick this box if you require a signature for delivery.`}</CheckboxInput2023>
    </section>
  );
};

const useRememberMyDetails = (
  formRef: React44ms.Api.TypedRef<CheckboxInput2023Expects>,
) => {
  const shippingClientStorageData =
    useDataMutableSourceShippingClientStorageData();

  const { shouldMigrateDeliveryDetails, handleMigrationComplete } =
    useMyPreviouslyFilledInInfo();
  const handleMigration = React.useCallback(() => {
    const { lastSelected_authoriseDeliveryToBeLeftWithoutSignature } =
      shippingClientStorageData;
    if (
      lastSelected_authoriseDeliveryToBeLeftWithoutSignature !== undefined &&
      lastSelected_authoriseDeliveryToBeLeftWithoutSignature !== null
    ) {
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        formRef,
        !!lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
      );
    }
  }, [
    formRef,
    shippingClientStorageData.lastSelected_authoriseDeliveryToBeLeftWithoutSignature,
  ]);
  React.useEffect(() => {
    if (shouldMigrateDeliveryDetails) {
      handleMigration();
      handleMigrationComplete();
    }
  }, [shouldMigrateDeliveryDetails, handleMigration, handleMigrationComplete]);
};
